import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import { AnalysisApiService } from '@marmon/frontend/analysis';
import {
	AlgoliaInfiniteScrollBaseComponent,
	DestroyService,
} from '@marmon/frontend/common';
import { AlgoliaResponse } from '@marmon/frontend/pagination';
import { Analysis } from '@marmon/shared/types/analysis';
import { PageSize, SortMod } from '@marmon/shared/types/pagination';
import { AlgoliaUser, User } from '@marmon/shared/types/user';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import { TuiButton, TuiIcon, TuiInput } from '@taiga-ui/core';
import { TuiButtonLoading, TuiTabs } from '@taiga-ui/kit';
import { TuiSearch } from '@taiga-ui/layout';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';
import { UserApiService } from '../../services';
import { AlgoliaUserRowComponent } from './algolia-user-row/algolia-user-row.component';

type Tab = {
	label: string;
	key: string;
};

const Tabs: Tab[] = [
	{ label: 'analyses.dialog.share-analysis.shared-with.tab', key: 'shared' },
	{ label: 'analyses.dialog.share-analysis.all-users.tab', key: 'all' },
] as const;

@Component({
	selector: 'mar-share-algolia-users-list',
	imports: [
		AsyncPipe,
		TranslocoPipe,
		TuiIcon,
		TuiAutoFocus,
		TuiSearch,
		TuiInput,
		FormsModule,
		TuiTabs,
		NgTemplateOutlet,
		TuiButton,
		AlgoliaUserRowComponent,
		TuiButtonLoading,
	],
	standalone: true,
	templateUrl: 'share-algolia-users-list.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
	host: { class: 'flex flex-col h-full' },
})
export class ShareAlgoliaUsersListComponent extends AlgoliaInfiniteScrollBaseComponent<
	AlgoliaUser,
	null
> {
	readonly alerts = inject(AlertService);
	readonly cdRef = inject(ChangeDetectorRef);
	readonly analysisApiService = inject(AnalysisApiService);
	readonly userApiService = inject(UserApiService);
	readonly analysis = input.required<Analysis>();
	readonly tabs = Tabs;

	readonly tabIndex = 0;
	get activeTab() {
		const tab = this.tabs[this.tabIndex];
		if (!tab) return this.tabs[0];
		return tab;
	}

	emails = new Set<string>();
	sharedWithSet = new Set<string>();

	readonly processing = signal<Set<string>>(new Set());
	readonly loadingSharedWith = signal<boolean>(true);

	readonly allSharedWith$ = new BehaviorSubject<User[]>([]);
	readonly sharedWith$ = combineLatest([
		this.allSharedWith$,
		this.search$.pipe(map((s) => s.trim().toLowerCase())),
	]).pipe(
		debounceTime(0),
		map(([data, search]) =>
			data.filter((d) => {
				if (d.displayName.toLowerCase().includes(search)) return true;
				if (d.email.toLowerCase().includes(search)) return true;
				if ((d.domain ?? '').toLowerCase().includes(search))
					return true;
				return false;
			}),
		),
	);

	override request$ = (
		options: [
			string,
			number,
			PageSize,
			keyof AlgoliaUser | null,
			SortMod,
			null,
			void,
		],
	) =>
		of(options).pipe(
			debounceTime(0),
			switchMap(([query, page]) =>
				this.userApiService.fetchAlgolia({ query, size: 10, page }),
			),
			map((response: AlgoliaResponse<AlgoliaUser>) => {
				const items = [];
				if (response.page !== 0) {
					items.push(...this.paginatedObject.items);
				}

				items.push(
					...response.hits.map((data) => ({
						objectID: data.objectID,
						data,
					})),
				);

				return this.paginatedObject.update({
					page: response.page,
					totalElements: response.nbHits,
					items,
				});
			}),
			tap((value) => {
				this.emails = new Set(
					value.items.map((i) => i.data.email.toLowerCase().trim()),
				);
			}),
		);

	constructor() {
		super();
		effect(() => {
			this.fetchSharedWith(this.analysis());
		});
	}

	fetchSharedWith(analysis: Analysis) {
		this.loadingSharedWith.set(true);
		return Promise.all([
			...analysis.shared.map((s) => this.userApiService.findById(s.id)),
		])
			.then((res) => {
				this.sharedWithSet = new Set(res.map((r) => r.id));
				this.allSharedWith$.next(res);
			})
			.finally(() => this.loadingSharedWith.set(false));
	}

	removeProcessing(id: string) {
		const value = this.processing();
		value.delete(id);
		this.cdRef.detectChanges();
	}

	addProcessing(id: string) {
		const value = this.processing();
		value.add(id);
		this.cdRef.detectChanges();
	}

	onInvite(email: string) {
		window.alert('Invite ' + email);
	}

	onShare(user: AlgoliaUser) {
		this.addProcessing(user.objectID);
		return this.analysisApiService
			.shareAnalysisById({
				analysisId: this.analysis().id,
				userId: user.objectID,
			})
			.catch((error) => {
				this.alerts.open('generic-error').subscribe();
				console.error(error);
				throw Error(error);
			})
			.then((res) => this.fetchSharedWith(res))
			.then(() => {
				this.refetch$.next();
				this.alerts.open('generic-success').subscribe();
			})
			.finally(() => {
				this.removeProcessing(user.objectID);
			});
	}

	onRemove(user: User) {
		this.addProcessing(user.id);
		return this.analysisApiService
			.removeShareAnalysisById({
				analysisId: this.analysis().id,
				userId: user.id,
			})
			.catch((error) => {
				this.alerts.open('generic-error').subscribe();
				console.error(error);
				throw Error(error);
			})
			.then((res) => this.fetchSharedWith(res))
			.then(() => {
				this.refetch$.next();
				this.alerts.open('generic-success').subscribe();
			})
			.finally(() => {
				this.removeProcessing(user.id);
			});
	}
}
